/* ======================================================================
   3D SCENE — Three.js r128 (global THREE, classic script)
   Reads pre-computed box coordinates and builds the mesh.
   All textures are procedural: no image assets to relink.
   Look: dark studio background, grey grid floor, pale wood pallets,
   cardboard cases with labels, dark "PALLET n" pills.
   ====================================================================== */

let renderer, scene, camera, groupRoot;
let theta=-0.7, phi=1.46, radius=160, target=new THREE.Vector3(0,26,0);
let framedOnce=false, userZoomed=false, lastPallets=[];
const GAP=22, ROWGAP=30;
const TAU=Math.PI*2;
const wrap=document.getElementById('canvasWrap');

/* ---- look & feel: change colours here ---- */
const LOOK={
  bg:0x141414, floor:0x8D8C8B, gridMajor:0x3f3e3d, gridMinor:0x5c5b5a, shadowColor:0x606164, shadowOpacity:1,
  woods:[0xdfc897,0xd4ba85,0xe6d1a3,0xcbae78],
  cardTop:'#e7cd9d', cardMid:'#d8b782', cardBot:'#c9a86f', tape:'rgba(236,217,173,.85)',
  edge:0x7d6136, edgeOpacity:.35,
  labelBg:'rgba(70,70,70,.93)', labelEdge:'rgba(140,140,140,.55)', labelText:'#e8c67d'
};

function wrapAngle(a){ a%=TAU; return a<0 ? a+TAU : a; }

function initThree(){
  renderer = new THREE.WebGLRenderer({antialias:true});
  renderer.setPixelRatio(Math.min(window.devicePixelRatio,2));
  renderer.shadowMap.enabled=true;
  renderer.shadowMap.type=THREE.PCFShadowMap;      /* hard, solid shadow edges */
  renderer.outputEncoding=THREE.sRGBEncoding;
  renderer.toneMapping=THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure=1.0;
  wrap.appendChild(renderer.domElement);

  scene=new THREE.Scene();
  scene.background=new THREE.Color(LOOK.bg);
  scene.fog=new THREE.Fog(LOOK.bg,700,2400);

  camera=new THREE.PerspectiveCamera(45,1,0.1,4000);

  scene.add(new THREE.HemisphereLight(0xffffff,0x444444,.75));
  scene.add(new THREE.AmbientLight(0xffffff,.35));
  const key=new THREE.DirectionalLight(0xfff4e2,1.0);
  key.position.set(95,165,90); key.castShadow=true;
  key.shadow.mapSize.set(2048,2048);
  key.shadow.camera.left=-170; key.shadow.camera.right=170;
  key.shadow.camera.top=170; key.shadow.camera.bottom=-170;
  key.shadow.camera.far=600; key.shadow.bias=-0.0006; key.shadow.radius=1;
  scene.add(key);
  const fill=new THREE.DirectionalLight(0xdfe6f5,.28); fill.position.set(-90,70,-60); scene.add(fill);
  const rim=new THREE.DirectionalLight(0xfff4e4,.18); rim.position.set(-40,90,120); scene.add(rim);

  /* floor — exact colour, not affected by lights or tone mapping */
  const ground=new THREE.Mesh(new THREE.PlaneGeometry(4000,4000),
    new THREE.MeshBasicMaterial({color:LOOK.floor, toneMapped:false}));
  ground.rotation.x=-Math.PI/2; ground.position.y=-0.1;
  scene.add(ground);

  /* shadow catcher sitting on the floor */
  const shadowPlane=new THREE.Mesh(new THREE.PlaneGeometry(4000,4000),
    new THREE.ShadowMaterial({color:LOOK.shadowColor, opacity:LOOK.shadowOpacity, toneMapped:false}));
  shadowPlane.rotation.x=-Math.PI/2; shadowPlane.position.y=-0.05; shadowPlane.receiveShadow=true;
  scene.add(shadowPlane);

  const grid=new THREE.GridHelper(4000,120,LOOK.gridMajor,LOOK.gridMinor);
  grid.position.y=0.02; grid.material.transparent=true; grid.material.opacity=.9;
  grid.material.toneMapped=false; scene.add(grid);

  groupRoot=new THREE.Group(); scene.add(groupRoot);

  addOrbit();
  resize();
  window.addEventListener('resize', resize);
  animate();
}

function resize(){
  const w=wrap.clientWidth, h=wrap.clientHeight;
  renderer.setSize(w,h);
  camera.aspect=w/h; camera.updateProjectionMatrix();
  frameStage(lastPallets, true);
}

let lastCount=-1;
let lastCfg=null;
function frameStage(pallets, isResize){
  const cfg=lastCfg||RULES;
  const count=Math.max((pallets||[]).filter(p=>p.cases>0).length,1);
  const rows=count>1?2:1, cols=Math.ceil(count/rows);
  const gridW=cols*cfg.W+(cols-1)*GAP, gridD=rows*cfg.L+(rows-1)*ROWGAP;
  const envH=cfg.deck+cfg.maxH+15;
  target.set(0,(cfg.deck+cfg.maxH)/2,0);
  const footprint=Math.hypot(gridW,gridD);
  const fit=fitRadius(footprint, envH, 0);

  if(!framedOnce){ radius=fit; framedOnce=true; lastCount=count; return; }
  if(count!==lastCount && !userZoomed){ radius=fit; lastCount=count; return; }
  lastCount=count;
  if(userZoomed) return;
  if(isResize){ radius=fit; return; }
  if(fit>radius) radius=fit;
}

function animate(){
  requestAnimationFrame(animate);
  const sp=Math.sin(phi), cp=Math.cos(phi), st=Math.sin(theta), ct=Math.cos(theta);
  camera.position.set(target.x+radius*sp*st, target.y+radius*cp, target.z+radius*sp*ct);
  camera.up.set(-cp*st, sp, -cp*ct);
  camera.lookAt(target);
  renderer.render(scene,camera);
}

let _hintT;
function nudgeHint(){
  const h=document.querySelector('.hint'); if(!h) return;
  h.classList.add('show');
  clearTimeout(_hintT); _hintT=setTimeout(()=>h.classList.remove('show'),1600);
}

const MIN_RADIUS=40, MAX_RADIUS=1400;

function clampRadius(r){ return Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, r)); }

function screenAxes(){
  return {
    right: new THREE.Vector3().setFromMatrixColumn(camera.matrix,0).normalize(),
    up:    new THREE.Vector3().setFromMatrixColumn(camera.matrix,1).normalize()
  };
}

function clampTarget(){
  const cfg=lastCfg||RULES;
  if(!cfg) return;
  const count=Math.max((lastPallets||[]).length,1);
  const rows=count>1?2:1, cols=Math.ceil(count/rows);
  const spanX=(cols*cfg.W+(cols-1)*GAP)/2+cfg.W;
  const spanZ=(rows*cfg.L+(rows-1)*ROWGAP)/2+cfg.L;
  const topY=cfg.deck+cfg.maxH+30;
  target.x=Math.max(-spanX,Math.min(spanX,target.x));
  target.z=Math.max(-spanZ,Math.min(spanZ,target.z));
  target.y=Math.max(0,Math.min(topY,target.y));
}

function pointerNdc(e, el){
  const r=el.getBoundingClientRect();
  if(!r.width || !r.height) return null;
  return { x:((e.clientX-r.left)/r.width)*2-1, y:-(((e.clientY-r.top)/r.height)*2-1) };
}

function zoomTo(next, ndc){
  const before=radius;
  radius=next;
  if(!ndc || before<=0) return;
  const closed=(before-next)/before;
  if(closed<=0) return;
  const vFov=camera.fov*Math.PI/180;
  const h=2*Math.tan(vFov/2)*before, w=h*camera.aspect;
  const ax=screenAxes();
  target.addScaledVector(ax.right, ndc.x*w*0.5*closed)
        .addScaledVector(ax.up,    ndc.y*h*0.5*closed);
  clampTarget();
}

function addOrbit(){
  const el=renderer.domElement;
  let dragging=false, panning=false, lx=0, ly=0, hovering=false;

  el.addEventListener('mousedown',e=>{
    dragging = e.button===0 && !e.shiftKey;
    panning  = e.button===1 || e.button===2 || (e.button===0 && e.shiftKey);
    lx=e.clientX; ly=e.clientY;
    if(panning) e.preventDefault();
  });
  window.addEventListener('mouseup',()=>{ dragging=false; panning=false; });
  window.addEventListener('mousemove',e=>{
    const dx=e.clientX-lx, dy=e.clientY-ly; lx=e.clientX; ly=e.clientY;
    if(dragging){ theta=wrapAngle(theta-dx*0.005); phi=wrapAngle(phi-dy*0.005); }
    if(panning){
      const ax=screenAxes(), s=radius*0.0016;
      target.addScaledVector(ax.right,-dx*s).addScaledVector(ax.up,dy*s);
      clampTarget();
    }
  });
  el.addEventListener('contextmenu',e=>e.preventDefault());
  el.addEventListener('mouseenter',()=>{ hovering=true; });
  el.addEventListener('mouseleave',()=>{ hovering=false; dragging=false; panning=false; });

  el.addEventListener('wheel',e=>{
    const modifier = e.ctrlKey || e.metaKey;
    if(!modifier && !(userZoomed && hovering)){ nudgeHint(); return; }

    const next=clampRadius(radius+e.deltaY*0.35);
    if(next===radius && !modifier) return;

    e.preventDefault();
    userZoomed=true;
    zoomTo(next, pointerNdc(e, el));
  },{passive:false});

  let tcx=0,tcy=0,tdist=0;
  const mid=e=>({x:(e.touches[0].clientX+e.touches[1].clientX)/2,
                 y:(e.touches[0].clientY+e.touches[1].clientY)/2});
  const centroid=e=>{
    let x=0,y=0;
    for(let i=0;i<e.touches.length;i++){ x+=e.touches[i].clientX; y+=e.touches[i].clientY; }
    return {x:x/e.touches.length, y:y/e.touches.length};
  };
  el.addEventListener('touchstart',e=>{
    if(e.touches.length===1) nudgeHint();
    if(e.touches.length===2){ const m=mid(e); tcx=m.x; tcy=m.y; tdist=touchDist(e); }
    if(e.touches.length>=3){ const m=centroid(e); tcx=m.x; tcy=m.y; }
  },{passive:true});
  el.addEventListener('touchmove',e=>{
    if(e.touches.length>=3){
      e.preventDefault();
      const m=centroid(e), dx=m.x-tcx, dy=m.y-tcy; tcx=m.x; tcy=m.y;
      const ax=screenAxes(), s=radius*0.0016;
      target.addScaledVector(ax.right,-dx*s).addScaledVector(ax.up,dy*s);
      clampTarget();
      return;
    }
    if(e.touches.length!==2) return;                 /* let the page scroll */
    e.preventDefault();
    const m=mid(e), dx=m.x-tcx, dy=m.y-tcy; tcx=m.x; tcy=m.y;
    theta=wrapAngle(theta-dx*0.006); phi=wrapAngle(phi-dy*0.006);
    userZoomed=true; const d=touchDist(e);
    zoomTo(clampRadius(radius+(tdist-d)*0.9), null); tdist=d;
  },{passive:false});
  function touchDist(e){ const a=e.touches[0], b=e.touches[1]; return Math.hypot(a.clientX-b.clientX,a.clientY-b.clientY); }
}

function fitRadius(width, height, depth){
  const vFov=camera.fov*Math.PI/180;
  const aspect=(camera && camera.aspect)?camera.aspect:1.6;
  const hFov=2*Math.atan(Math.tan(vFov/2)*aspect);
  const halfW=width/2+depth/2, halfH=height/2+depth/2;
  const distW=halfW/Math.tan(hFov/2), distH=halfH/Math.tan(vFov/2);
  return Math.max(150, Math.max(distW,distH)*1.35);
}

/* ---- procedural cardboard box textures (no image assets) ---- */
const _boxMatCache={};
function boxMaterials(color){
  if(_boxMatCache[color]) return _boxMatCache[color];
  const side=new THREE.CanvasTexture(makeCardboardCanvas(color,false));
  const top =new THREE.CanvasTexture(makeCardboardCanvas(color,true));
  [side,top].forEach(t=>{ t.encoding=THREE.sRGBEncoding; t.anisotropy=8; });
  const mk = tex => new THREE.MeshStandardMaterial({map:tex, roughness:.92, metalness:0});
  const mats=[mk(side),mk(side),mk(top),mk(side),mk(side),mk(side)];
  _boxMatCache[color]=mats;
  return mats;
}
function makeCardboardCanvas(color,isTop){
  const c=document.createElement('canvas'); c.width=c.height=256;
  const x=c.getContext('2d');
  const g=x.createLinearGradient(0,0,0,256);
  g.addColorStop(0,LOOK.cardTop); g.addColorStop(.5,LOOK.cardMid); g.addColorStop(1,LOOK.cardBot);
  x.fillStyle=g; x.fillRect(0,0,256,256);
  for(let i=0;i<2600;i++){
    const px=Math.random()*256, py=Math.random()*256, a=Math.random()*0.06;
    x.fillStyle=`rgba(90,60,25,${a})`; x.fillRect(px,py,1,1);
  }
  x.strokeStyle='rgba(120,85,40,.10)'; x.lineWidth=1;
  for(let yy=6; yy<256; yy+=9){ x.beginPath(); x.moveTo(0,yy); x.lineTo(256,yy); x.stroke(); }
  if(isTop){
    x.strokeStyle='rgba(95,62,28,.40)'; x.lineWidth=2;
    x.beginPath(); x.moveTo(128,0); x.lineTo(128,256); x.stroke();
    x.fillStyle=LOOK.tape; x.fillRect(104,0,48,256);
    x.strokeStyle='rgba(255,255,255,.22)'; x.lineWidth=1;
    x.beginPath(); x.moveTo(107,0); x.lineTo(107,256); x.stroke();
    x.beginPath(); x.moveTo(149,0); x.lineTo(149,256); x.stroke();
  }else{
    /* shipping label with a solid colour header */
    x.fillStyle='rgba(252,251,248,.97)'; roundRect(x,138,142,104,82,6); x.fill();
    x.fillStyle=color; roundRect(x,138,142,104,26,6); x.fill(); x.fillRect(138,160,104,8);
    x.fillStyle='rgba(60,60,60,.6)';
    for(let ln=0; ln<4; ln++) x.fillRect(148,182+ln*11,66-ln*9,5);
    x.strokeStyle='rgba(60,45,25,.28)'; x.lineWidth=2;
    x.strokeRect(138,142,104,82);
  }
  return c;
}

/* ---- slatted wooden pallet — pale wood like the reference ---- */
function buildPalletDeck(g, cfg){
  if(!(cfg.deck > 0)) return;

  const woods=LOOK.woods;
  const woodMat=(i)=>new THREE.MeshStandardMaterial({color:woods[i%woods.length], roughness:.9, metalness:0});
  const th=1.3, topY=cfg.deck-th/2;
  const n=7, bw=3.6, span=cfg.L, step=(span-bw)/(n-1);
  for(let i=0;i<n;i++){
    const board=new THREE.Mesh(new THREE.BoxGeometry(cfg.W, th, bw), woodMat(i));
    board.position.set(0, topY, -span/2+bw/2+i*step);
    board.castShadow=true; board.receiveShadow=true; g.add(board);
  }
  const strH=cfg.deck-th-0.2, strY=(cfg.deck-th)/2-0.1;
  [-cfg.W/2+2.5, 0, cfg.W/2-2.5].forEach((px,i)=>{
    const s=new THREE.Mesh(new THREE.BoxGeometry(3.2, strH, cfg.L), woodMat(i+3));
    s.position.set(px, strY, 0); s.castShadow=true; s.receiveShadow=true; g.add(s);
  });
  [-cfg.L/2+bw/2, 0, cfg.L/2-bw/2].forEach((pz,i)=>{
    const b=new THREE.Mesh(new THREE.BoxGeometry(cfg.W, th, bw*1.15), woodMat(i+2));
    b.position.set(0, th/2, pz); b.castShadow=true; b.receiveShadow=true; g.add(b);
  });
}

/* ---- "PALLET n" pill: dark rounded tag with gold text ---- */
function makeLabel(text, g, cfg){
  const SS = 2;
  const fs = 30, padX = 26, padY = 13;
  const font = '800 ' + fs + 'px system-ui, Segoe UI, Arial, sans-serif';
  text = String(text).toUpperCase();

  const meas = document.createElement('canvas').getContext('2d');
  meas.font = font;
  const W = Math.ceil(meas.measureText(text).width) + padX*2;
  const H = fs + padY*2;

  const c = document.createElement('canvas');
  c.width = W*SS; c.height = H*SS;
  const ctx = c.getContext('2d');
  ctx.scale(SS, SS);

  roundRect(ctx, 1, 1, W-2, H-2, 8);
  ctx.fillStyle = LOOK.labelBg; ctx.fill();
  ctx.strokeStyle = LOOK.labelEdge; ctx.lineWidth = 1.5; ctx.stroke();

  ctx.fillStyle = LOOK.labelText;
  ctx.font = font;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(text, W/2, H/2 + 1);

  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 8; tex.encoding = THREE.sRGBEncoding;
  const spr = new THREE.Sprite(new THREE.SpriteMaterial({map:tex, transparent:true, depthWrite:false}));
  const worldH = 9;
  spr.scale.set(worldH * (W/H), worldH, 1);
  spr.position.set(0, cfg.deck + cfg.maxH + 11, 0);
  g.add(spr);
}
function roundRect(ctx,x,y,w,h,r){
  ctx.beginPath(); ctx.moveTo(x+r,y);
  ctx.arcTo(x+w,y,x+w,y+h,r); ctx.arcTo(x+w,y+h,x,y+h,r);
  ctx.arcTo(x,y+h,x,y,r); ctx.arcTo(x,y,x+w,y,r); ctx.closePath();
}

/* Renders the ENTIRE scene from a packed result (full rebuild each call). */
function renderPallets(result){

  groupRoot.traverse(o=>{
    if(o.geometry) o.geometry.dispose();
    const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
    mats.forEach(m=>{
      if(o.isSprite){ if(m.map) m.map.dispose(); m.dispose(); return; }
      if(!m.map) m.dispose();
    });
  });
  while(groupRoot.children.length) groupRoot.remove(groupRoot.children[0]);
  const cfg=result.cfg, pallets=result.pallets;
  lastCfg=cfg;

  const rows=pallets.length>1?2:1, cols=Math.ceil(pallets.length/rows);
  const totalD=rows*cfg.L+(rows-1)*ROWGAP, startZ=-totalD/2;

  pallets.forEach((p,idx)=>{
    const row=Math.floor(idx/cols), col=idx%cols;
    const inThisRow=Math.min(cols, pallets.length-row*cols);
    const rowW=inThisRow*cfg.W+(inThisRow-1)*GAP, rowStartX=-rowW/2;
    const x=rowStartX+col*(cfg.W+GAP)+cfg.W/2;
    const z=startZ+row*(cfg.L+ROWGAP)+cfg.L/2;

    const pcfg = p.cfg || cfg;

    const g=new THREE.Group();
    g.position.set(x,0,z);
    buildPalletDeck(g,pcfg);

    p.boxes.forEach(b=>{
      const geo=new THREE.BoxGeometry(b.w,b.h,b.l);
      const mesh=new THREE.Mesh(geo, boxMaterials(b.color));
      mesh.castShadow=true; mesh.receiveShadow=true;
      mesh.position.set(-pcfg.W/2+b.x+b.w/2, pcfg.deck+b.y+b.h/2, -pcfg.L/2+b.z+b.l/2);
      g.add(mesh);
      const edges=new THREE.LineSegments(new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({color:LOOK.edge, transparent:true, opacity:LOOK.edgeOpacity}));
      edges.position.copy(mesh.position);
      g.add(edges);
    });

    makeLabel(`PALLET ${idx+1}`, g, cfg);
    groupRoot.add(g);
  });

  lastPallets=pallets;
  frameStage(pallets,false);
}